import { defineStore } from 'pinia'
import { splitName } from '@/utils/strings'
import { useCartStore } from '@/store/cart'

export const useCheckoutStore = defineStore('checkout', {
    state: () => ({
        mode: '' as CheckoutMode,
        userSelections: {
            selectedShippingId: null as string|null,
            selectedHardwarePriceId: null as string|null,
        },
        coach: null,
        fullName: '',
        first_name: '',
        last_name: '',
        shippingFullName: '',
        phone: '',
        email: '',
        password: '',
        accountValidated: false,
        username: '',
        shippingAddress: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US',
        } as AddressFields,
        billingAddress: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US',
        } as AddressFields,
        steps: {
            showOrderDetails: {
                open: false,
            },
            createAccount: {
                open: true,
                complete: false,
            },
            selectEquipment: {
                open: false,
                complete: false,
            },
            paymentMethod: {
                open: false,
                complete: false,
            },
        },
        selectedHardwareQty: 1,
        membershipOrder: null as MembershipOrder | null,
        equipmentOrder: null as EquipmentOrder | null,
        membershipPurchaseComplete: false,
        orderParams: null as CheckoutParams | null,
        paymentMethodId: '',
        stripeCustomerId: '',
        accountId: undefined as number | undefined,
        giftOrder: null as GiftIntentResponse | null,
        giftCode: '',
        paymentIntent: null as PaymentIntent | null,
        orgName: '',
        org: null as Org | null,
    }),
    getters: {
        taxableAddress(): AddressFields | null {
            if (this.shippingAddress.postal_code) {
                return this.shippingAddress
            }
            if (this.billingAddress.postal_code) {
                return this.billingAddress
            }
            return null
        },
    },
    actions: {
        getCheckoutParams(): CheckoutParams {
            const cart = useCartStore()
            const requiresShipping = cart.requireShipping
            const accountFullName = splitName(this.fullName || this.shippingFullName || '')
            const shippingFullName = splitName(this.shippingFullName || this.fullName || '')

            const address = this.billingAddress
            const shipping: ShippingAddressFields|undefined = requiresShipping && shippingFullName?.first_name && shippingFullName?.last_name ? {
                name: `${shippingFullName?.first_name} ${shippingFullName?.last_name}`,
                first_name: shippingFullName?.first_name,
                last_name: shippingFullName?.last_name,
                phone: this.phone || undefined,
                address: this.shippingAddress,
            } : undefined

            if (shipping && !shipping?.address?.postal_code) shipping.address = address

            // /accounts/coupon/{code} -- used for subscription only -- type: 'membership'
            const coupon = cart.$state.couponPromo?.type === 'membership' ? cart.$state.couponPromo?.code : undefined

            // /products/promo/{code} -- used for equipment only -- type: 'promo'
            const coupon_code = this.mode === 'equipment' && cart.$state.couponPromo?.type === 'promo'
                ? cart.$state.couponPromo?.code
                : undefined

            const product_ids = cart.selectedHardware.map((hardware) => hardware.wc_id)
            const plan = cart.selectedPlan?.stripe_price_id || undefined

            let evolvUid = undefined
            try {
                evolvUid = evolv?.context?.uid
            } catch (error) {
                //
            }

            const trial_days = this.mode === 'trial' ? 14 : this.mode === 'org' ? cart.selectedPlan?.trial_days : undefined

            return {
                name: `${accountFullName.first_name} ${accountFullName.last_name}`,
                first_name: accountFullName.first_name,
                last_name: accountFullName.last_name,
                username: this.username || undefined,
                email: this.email || undefined,
                password: this.password || undefined,
                phone: this.phone,
                address,
                shipping,
                payment_method: this.paymentMethodId || undefined,
                plan,
                product_ids,
                product_quantity: requiresShipping ? this.selectedHardwareQty || 1 : 1,
                coupon,
                coupon_code,
                promo: undefined, // @techdebt: I don't know what this is for
                discount_code: undefined, // @techdebt: I don't know what this is for
                trial_days,
                trial_code: undefined,
                referral_code: undefined,
                expedited_shipping: cart.shipping === 'expedited' ? 'true' : undefined,
                product_tax_rate: cart.taxResponse?.taxes?.[0]?.tax_rate || undefined,
                evolv_uid: evolvUid,
            }
        },
    },
})
